import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import adminApi from "../../api/adminApi";
import authApi from "../../api/authApi";
import FormInput from "../../components/FormInput/FormInput";
import Select from "../../components/Select/Select";
import Button from "../../components/Button/Button";
import "./CreateUser.css";

const ROLE_OPTIONS = [
  { value: "student", label: "Student" },
  { value: "mentor", label: "Mentor" },
  { value: "batch manager", label: "Batch Manager" },
  { value: "doubt solver", label: "Doubt Solver" },
  { value: "admin", label: "Admin" },
];

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

const CreateUser = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    mobileNumber: "",
    dateOfBirth: "",
    gender: "",
    address: "",
    role: "student",
    termsAccepted: true,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Determine which endpoint to use based on role
      let response;
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        mobileNumber: formData.mobileNumber,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        address: formData.address,
        termsAccepted: formData.termsAccepted,
      };

      if (formData.role === "student") {
        // Use student signup endpoint
        payload.selectedCourse = null; // Can be set later
        response = await authApi.post("/auth/signup/student", payload);
      } else if (
        ["mentor", "batch manager", "doubt solver"].includes(formData.role)
      ) {
        // For staff roles, create via user endpoint (public endpoint forces student role)
        // Then update role via admin endpoint
        const createResponse = await adminApi.post("/users", payload);
        if (createResponse.data?._id) {
          // Update role after creation
          response = await adminApi.patch(
            `/users/${createResponse.data._id}/role`,
            {
              role: formData.role,
            }
          );
          response.data = { user: response.data, _id: response.data._id };
        } else {
          response = createResponse;
        }
      } else if (formData.role === "admin") {
        // Use admin signup endpoint (may require invite code for non-superadmin)
        payload.requestedRole = "admin";
        response = await authApi.post("/auth/signup/admin", payload);
      }

      if (response?.data) {
        navigate(`/users/${response.data.user?._id || response.data._id}`);
      } else {
        navigate("/users");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to create user"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-user-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">User Management</p>
          <h1>Create New User</h1>
          <p className="subhead">Add a new user to the system</p>
        </div>
        <Button variant="secondary" onClick={() => navigate("/users")}>
          Cancel
        </Button>
      </header>

      <form className="create-user-form" onSubmit={handleSubmit}>
        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}

        <div className="form-section">
          <h2>Basic Information</h2>
          <div className="form-grid">
            <FormInput
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
            <FormInput
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
            <FormInput
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            <FormInput
              label="Phone Number"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleChange}
              required
            />
            <FormInput
              label="Date of Birth"
              name="dateOfBirth"
              type="date"
              value={formData.dateOfBirth}
              onChange={handleChange}
              required
            />
            <Select
              label="Gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              options={GENDER_OPTIONS}
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Account Details</h2>
          <div className="form-grid">
            <FormInput
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
            />
            <Select
              label="Role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              options={ROLE_OPTIONS}
              required
            />
            <FormInput
              label="Address"
              name="address"
              type="textarea"
              value={formData.address}
              onChange={handleChange}
              required
              rows={3}
            />
          </div>
        </div>

        <div className="form-actions">
          <Button
            variant="secondary"
            type="button"
            onClick={() => navigate("/users")}
          >
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            Create User
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateUser;
